'use client';

import { getZodiacSignFromDate, ZODIAC_SIGNS_DATA } from '@repo/lib';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Orbit, Share2, Sparkles, Star } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './onboarding.module.css';

// 10 screens: goal → pain → solution → preferences → birthData → processing → demo → value → account → paywall

const TOTAL_SCREENS = 10;
const DEMO_PICK_LIMIT = 3;
const DEMO_VISIBLE_CARDS = 6;
const PROCESSING_AUTO_ADVANCE_MS = 3000;
const STAR_COUNT = 5;
const PROGRESS_SCALE = 100;
const SCREEN_TRANSITION_DURATION = 0.28;
const SCREEN_EASE_CURVE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const JOURNEY_GOAL_SCORE = 4;
const JOURNEY_PAIN_SCORE = 2;
const JOURNEY_PREF_SCORE = 1;
const STAR_SLOTS = ['s1', 's2', 's3', 's4', 's5'] as const;
type OnboardingLocale = 'pt' | 'en' | 'es';
type OnboardingCopy = (typeof ONBOARDING_COPY)['pt'];
const ONBOARDING_COPY = {
  pt: {
    goalEyebrow: 'ETAPA 1 · O QUE TROUXE VOCÊ ATÉ AQUI',
    goalTitle: 'O que você quer do céu agora?',
    goalLead: 'Escolha a opção mais próxima. Não há resposta errada.',
    choiceLabels: {
      'Understand myself deeper': 'Entender melhor quem sou',
      'Navigate a relationship': 'Navegar um relacionamento',
      'Daily guidance & clarity': 'Orientação e clareza diárias',
      'Track life cycles': 'Acompanhar ciclos da vida',
      'Ask the cards a question': 'Fazer uma pergunta às cartas',
      'Find grounding & ritual': 'Encontrar equilíbrio e ritual',
      'Horoscopes feel too generic': 'Os horóscopos parecem genéricos demais',
      "I can't make sense of what I'm feeling": 'Não consigo entender o que estou sentindo',
      'Other apps feel noisy and superficial': 'Outros apps parecem barulhentos e superficiais',
      "I don't know my full birth chart": 'Não conheço meu mapa natal completo',
      'I question my relationships': 'Questiono meus relacionamentos',
      'I lack a grounding daily practice': 'Não tenho uma prática diária de equilíbrio',
      'Tarot readings': 'Leituras de tarô',
      'Oracle conversations': 'Conversas com o Oráculo',
      'Natal chart deep-dives': 'Análises profundas do mapa natal',
      'Relationship insights': 'Insights sobre relacionamentos',
      'Moon & cycle tracking': 'Acompanhamento da Lua e dos ciclos',
      'Journaling prompts': 'Perguntas para escrever no diário',
      'Birth chart, big three, inner patterns': 'Mapa natal, trio principal e padrões internos',
      'Compatibility, connection, timing': 'Compatibilidade, conexão e timing',
      'Cosmic weather, what today holds': 'Clima cósmico e o que o dia reserva',
      'Moon phases, retrogrades, seasons': 'Fases da Lua, retrogradações e estações',
      'Tarot pulls for real moments': 'Tiragens de tarô para momentos reais',
      'A quiet practice to return to': 'Uma prática tranquila para voltar a si',
      'Same paragraph for everyone with my sign': 'O mesmo parágrafo para todos do meu signo',
      'Emotions feel scattered or overwhelming': 'As emoções parecem dispersas ou intensas',
      'Too many ads, too little depth': 'Anúncios demais e pouca profundidade',
      'Only know my sun sign, if that': 'Só conheço meu signo solar, se tanto',
      'Unclear if the connection is right': 'Não sei se a conexão é certa',
      'Nothing centers me in the morning': 'Nada me traz equilíbrio pela manhã',
    },
    name: 'Nome completo',
    painTitle: 'O que não está funcionando agora?',
    painLead: 'Seja sincero: escolha tudo que fizer sentido.',
    solutionEyebrow: 'ETAPA 3 · VEJA COMO AJUDAMOS',
    preferencesEyebrow: 'ETAPA 4 · DEIXE COM A SUA CARA',
    preferencesTitle: 'O que chama você?',
    preferencesLead: 'Comece pela perspectiva que combina com seu momento.',
    birthEyebrow: 'ETAPA 5 · SEUS DADOS DE NASCIMENTO',
    birthTitle: 'Insira seus dados. Vamos ler seu mapa.',
    birthLead: 'O céu do dia em que você nasceu guarda seu mapa original. Leva 60 segundos.',
    dob: 'Data de nascimento',
    time: 'Hora de nascimento',
    optional: '(opcional)',
    place: 'Local de nascimento',
    cityPlaceholder: 'Cidade, país',
    continue: 'Continuar',
    back: 'Voltar',
  },
  en: {
    goalEyebrow: 'STEP 1 · WHAT BROUGHT YOU HERE',
    goalTitle: 'What do you want from the sky right now?',
    goalLead: "Pick the one that feels closest. There's no wrong answer.",
    painTitle: "What's not working right now?",
    painLead: 'Be honest — pick everything that fits.',
    solutionEyebrow: 'STEP 3 · SEE HOW WE HELP',
    choiceLabels: {
      'Understand myself deeper': 'Understand myself deeper',
      'Navigate a relationship': 'Navigate a relationship',
      'Daily guidance & clarity': 'Daily guidance & clarity',
      'Track life cycles': 'Track life cycles',
      'Ask the cards a question': 'Ask the cards a question',
      'Find grounding & ritual': 'Find grounding & ritual',
      'Horoscopes feel too generic': 'Horoscopes feel too generic',
      "I can't make sense of what I'm feeling": "I can't make sense of what I'm feeling",
      'Other apps feel noisy and superficial': 'Other apps feel noisy and superficial',
      "I don't know my full birth chart": "I don't know my full birth chart",
      'I question my relationships': 'I question my relationships',
      'I lack a grounding daily practice': 'I lack a grounding daily practice',
      'Tarot readings': 'Tarot readings',
      'Oracle conversations': 'Oracle conversations',
      'Natal chart deep-dives': 'Natal chart deep-dives',
      'Journaling prompts': 'Journaling prompts',
      'Birth chart, big three, inner patterns': 'Birth chart, big three, inner patterns',
      'Compatibility, connection, timing': 'Compatibility, connection, timing',
      'Cosmic weather, what today holds': 'Cosmic weather, what today holds',
      'Moon phases, retrogrades, seasons': 'Moon phases, retrogrades, seasons',
      'Tarot pulls for real moments': 'Tarot pulls for real moments',
      'A quiet practice to return to': 'A quiet practice to return to',
      'Same paragraph for everyone with my sign': 'Same paragraph for everyone with my sign',
      'Emotions feel scattered or overwhelming': 'Emotions feel scattered or overwhelming',
      'Too many ads, too little depth': 'Too many ads, too little depth',
      'Only know my sun sign, if that': 'Only know my sun sign, if that',
      'Unclear if the connection is right': 'Unclear if the connection is right',
      'Nothing centers me in the morning': 'Nothing centers me in the morning',
    },
    name: 'Full name',
    preferencesTitle: 'What draws you in?',
    preferencesLead: 'Start with the lens that fits your moment.',
    birthEyebrow: 'STEP 5 · YOUR BIRTH DATA',
    birthTitle: "Enter your details. Let's read your chart.",
    birthLead: 'The sky on the day you were born holds your original blueprint. Takes 60 seconds.',
    dob: 'Date of birth',
    time: 'Time of birth',
    optional: '(optional)',
    place: 'Place of birth',
    cityPlaceholder: 'City, Country',
    continue: 'Continue',
    back: 'Back',
  },
  es: {
    name: 'Nombre completo',
    goalEyebrow: 'PASO 1 · QUÉ TE TRAJO HASTA AQUÍ',
    goalTitle: '¿Qué quieres del cielo ahora?',
    goalLead: 'Elige la opción más cercana. No hay respuestas incorrectas.',
    painTitle: '¿Qué no está funcionando ahora?',
    painLead: 'Sé sincero: elige todo lo que encaje.',
    solutionEyebrow: 'PASO 3 · ASÍ ES COMO AYUDAMOS',
    preferencesEyebrow: 'PASO 4 · HAZLO TUYO',
    preferencesTitle: '¿Qué te atrae?',
    preferencesLead: 'Empieza por la perspectiva que encaje con tu momento.',
    birthEyebrow: 'PASO 5 · TUS DATOS DE NACIMIENTO',
    birthTitle: 'Introduce tus datos. Leamos tu carta.',
    birthLead: 'El cielo del día en que naciste guarda tu mapa original. Tarda 60 segundos.',
    dob: 'Fecha de nacimiento',
    time: 'Hora de nacimiento',
    optional: '(opcional)',
    place: 'Lugar de nacimiento',
    choiceLabels: {
      'Understand myself deeper': 'Entenderme mejor',
      'Navigate a relationship': 'Navegar una relación',
      'Daily guidance & clarity': 'Orientación y claridad diarias',
      'Track life cycles': 'Seguir los ciclos de la vida',
      'Ask the cards a question': 'Hacer una pregunta a las cartas',
      'Find grounding & ritual': 'Encontrar equilibrio y ritual',
      'Horoscopes feel too generic': 'Los horóscopos parecen demasiado genéricos',
      "I can't make sense of what I'm feeling": 'No entiendo lo que estoy sintiendo',
      'Other apps feel noisy and superficial': 'Otras apps parecen ruidosas y superficiales',
      "I don't know my full birth chart": 'No conozco mi carta natal completa',
      'I question my relationships': 'Cuestiono mis relaciones',
      'I lack a grounding daily practice': 'Me falta una práctica diaria de equilibrio',
      'Tarot readings': 'Lecturas de tarot',
      'Oracle conversations': 'Conversaciones con el Oráculo',
      'Natal chart deep-dives': 'Análisis profundos de la carta natal',
      'Relationship insights': 'Perspectivas sobre relaciones',
      'Moon & cycle tracking': 'Seguimiento de la Luna y los ciclos',
      'Journaling prompts': 'Preguntas para escribir en el diario',
      'Birth chart, big three, inner patterns': 'Carta natal, tríada principal y patrones internos',
      'Compatibility, connection, timing': 'Compatibilidad, conexión y timing',
      'Cosmic weather, what today holds': 'Clima cósmico y lo que trae el día',
      'Moon phases, retrogrades, seasons': 'Fases lunares, retrogradaciones y estaciones',
      'Tarot pulls for real moments': 'Tiradas de tarot para momentos reales',
      'A quiet practice to return to': 'Una práctica tranquila para volver a ti',
      'Same paragraph for everyone with my sign': 'El mismo párrafo para todos con mi signo',
      'Emotions feel scattered or overwhelming': 'Las emociones parecen dispersas o abrumadoras',
      'Too many ads, too little depth': 'Demasiados anuncios y poca profundidad',
      'Only know my sun sign, if that': 'Solo conozco mi signo solar, si acaso',
      'Unclear if the connection is right': 'No sé si la conexión es adecuada',
      'Nothing centers me in the morning': 'Nada me centra por la mañana',
    },
    cityPlaceholder: 'Ciudad, país',
    continue: 'Continuar',
    back: 'Atrás',
  },
} as const;

function useOnboardingCopy() {
  const localeValue = useLocale();
  const locale: OnboardingLocale =
    localeValue === 'pt' || localeValue === 'es' ? localeValue : 'en';
  return ONBOARDING_COPY[locale] as OnboardingCopy & { choiceLabels: Record<string, string> };
}

type ScreenId =
  | 'goal'
  | 'pain'
  | 'solution'
  | 'preferences'
  | 'birthData'
  | 'processing'
  | 'demo'
  | 'value'
  | 'account'
  | 'paywall';

const SCREEN_ORDER: ScreenId[] = [
  'goal',
  'pain',
  'solution',
  'preferences',
  'birthData',
  'processing',
  'demo',
  'value',
  'account',
  'paywall',
];

const TOTAL_PROGRESS_PHASES = 8;
const SCREEN_PROGRESS_PHASE: Record<ScreenId, number> = {
  goal: 1,
  pain: 2,
  solution: 3,
  preferences: 4,
  birthData: 5,
  processing: 6,
  demo: 6,
  value: 7,
  account: 7,
  paywall: 8,
};

type ChoiceOption = { emoji: string; label: string; sub: string };

const GOAL_OPTIONS: ChoiceOption[] = [
  { emoji: '🔮', label: 'Understand myself deeper', sub: 'Birth chart, big three, inner patterns' },
  { emoji: '💗', label: 'Navigate a relationship', sub: 'Compatibility, connection, timing' },
  { emoji: '✨', label: 'Daily guidance & clarity', sub: 'Cosmic weather, what today holds' },
  { emoji: '🌗', label: 'Track life cycles', sub: 'Moon phases, retrogrades, seasons' },
  { emoji: '🃏', label: 'Ask the cards a question', sub: 'Tarot pulls for real moments' },
  { emoji: '🌑', label: 'Find grounding & ritual', sub: 'A quiet practice to return to' },
];
const GOAL_CONTEXT: Record<string, string> = {
  'Understand myself deeper': 'a deeper understanding of yourself',
  'Navigate a relationship': 'a clearer read on a relationship',
  'Daily guidance & clarity': 'a signal for the days ahead',
  'Track life cycles': 'the rhythm beneath your current season',
  'Ask the cards a question': 'a direct answer to something on your mind',
  'Find grounding & ritual': 'a quieter place to return to',
};

const PAIN_OPTIONS: ChoiceOption[] = [
  {
    emoji: '😐',
    label: 'Horoscopes feel too generic',
    sub: 'Same paragraph for everyone with my sign',
  },
  {
    emoji: '😵‍💫',
    label: "I can't make sense of what I'm feeling",
    sub: 'Emotions feel scattered or overwhelming',
  },
  {
    emoji: '📱',
    label: 'Other apps feel noisy and superficial',
    sub: 'Too many ads, too little depth',
  },
  { emoji: '🤔', label: "I don't know my full birth chart", sub: 'Only know my sun sign, if that' },
  { emoji: '💔', label: 'I question my relationships', sub: 'Unclear if the connection is right' },
  {
    emoji: '🕐',
    label: 'I lack a grounding daily practice',
    sub: 'Nothing centers me in the morning',
  },
];

type SolutionMapping = { pain: string; fix: string; icon: string; stat: string };

const SOLUTION_MAPPINGS: SolutionMapping[] = [
  {
    pain: '"Horoscopes feel too generic"',
    fix: 'Readings built on your full birth chart — sun, moon, rising, and every planet that shapes you. Not a paragraph shared with 500 million people.',
    icon: '◎',
    stat: '10 planetary placements, uniquely yours — not 1 of 12 signs.',
  },
  {
    pain: '"I can\'t make sense of what I\'m feeling"',
    fix: 'Daily cosmic weather names the actual transits hitting your chart before you have to figure it out alone.',
    icon: '🌗',
    stat: 'Tracks 7 live planetary transits against your chart every day.',
  },
  {
    pain: '"Other apps feel noisy and superficial"',
    fix: 'A quiet observatory — no feeds, no ads, no noise. Just you and the sky.',
    icon: '◌',
    stat: "Zero ads. Zero social feed. No notifications you didn't ask for.",
  },
];

const PREF_OPTIONS: ChoiceOption[] = [
  { emoji: '🃏', label: 'Tarot readings', sub: '' },
  { emoji: '🔮', label: 'Oracle conversations', sub: '' },
  { emoji: '📊', label: 'Natal chart deep-dives', sub: '' },
  { emoji: '💕', label: 'Relationship insights', sub: '' },
  { emoji: '🌗', label: 'Moon & cycle tracking', sub: '' },
  { emoji: '📝', label: 'Journaling prompts', sub: '' },
];

type JourneyPath = {
  id: 'inner' | 'heart' | 'daily';
  label: string;
  focus: string;
  solutionTitle: string;
  solutionLead: string;
  processingLabel: string;
  processingSub: string;
  demoPrompt: string;
  readingLead: string;
  recommendedPrefs: readonly string[];
  goalLabels: readonly string[];
  painLabels: readonly string[];
};

const JOURNEY_PATHS: readonly JourneyPath[] = [
  {
    id: 'inner',
    label: 'Inner sky',
    focus: 'the patterns beneath the surface',
    solutionTitle: 'A reading built around your inner patterns.',
    solutionLead:
      'Your answers point toward depth, context, and a quieter way to make sense of yourself.',
    processingLabel: 'Mapping your inner sky...',
    processingSub: 'Connecting your patterns with the chart beneath them',
    demoPrompt: 'Choose three cards for the pattern asking to be seen.',
    readingLead: 'This spread meets you at the edge of a pattern that is ready to become clear.',
    recommendedPrefs: ['Natal chart deep-dives', 'Journaling prompts', 'Moon & cycle tracking'],
    goalLabels: ['Understand myself deeper', 'Find grounding & ritual', 'Track life cycles'],
    painLabels: [
      "I can't make sense of what I'm feeling",
      'I lack a grounding daily practice',
      "I don't know my full birth chart",
    ],
  },
  {
    id: 'heart',
    label: 'Heart orbit',
    focus: 'the connection you are trying to understand',
    solutionTitle: 'A reading that keeps the relationship in context.',
    solutionLead:
      'Your answers point toward compatibility, timing, and the part of the story that belongs to you.',
    processingLabel: 'Tracing your heart orbit...',
    processingSub: 'Pairing your relationship question with the sky around it',
    demoPrompt: 'Choose three cards for the connection asking for clarity.',
    readingLead: 'This spread keeps both the feeling and your own center in view.',
    recommendedPrefs: ['Relationship insights', 'Tarot readings', 'Natal chart deep-dives'],
    goalLabels: ['Navigate a relationship'],
    painLabels: ['I question my relationships'],
  },
  {
    id: 'daily',
    label: 'Daily signal',
    focus: 'the next small signal worth following',
    solutionTitle: 'A reading that meets you in the moment.',
    solutionLead:
      'Your answers point toward small signals you can return to when the day gets noisy.',
    processingLabel: 'Tuning into your daily signal...',
    processingSub: "Blending your answers with today's cosmic weather",
    demoPrompt: 'Choose three cards for the next signal worth following.',
    readingLead: 'This spread is less about prediction and more about the next honest step.',
    recommendedPrefs: ['Tarot readings', 'Oracle conversations', 'Moon & cycle tracking'],
    goalLabels: ['Daily guidance & clarity', 'Ask the cards a question'],
    painLabels: ['Horoscopes feel too generic', 'Other apps feel noisy and superficial'],
  },
];

type JourneyAnswers = {
  goal: string | null;
  pains: Set<string>;
  prefs: Set<string>;
};

function resolveJourneyPath({ goal, pains, prefs }: JourneyAnswers) {
  let bestPath = JOURNEY_PATHS[0];
  let bestScore = -1;
  for (const path of JOURNEY_PATHS) {
    let score = 0;
    if (goal && path.goalLabels.includes(goal)) {
      score += JOURNEY_GOAL_SCORE;
    }
    for (const pain of pains) {
      if (path.painLabels.includes(pain)) {
        score += JOURNEY_PAIN_SCORE;
      }
    }
    for (const pref of prefs) {
      if (path.recommendedPrefs.includes(pref)) {
        score += JOURNEY_PREF_SCORE;
      }
    }
    if (score > bestScore) {
      bestPath = path;
      bestScore = score;
    }
  }
  return bestPath;
}

function orderedPreferences(path: JourneyPath) {
  return [...PREF_OPTIONS].sort((a, b) => {
    const aIndex = path.recommendedPrefs.indexOf(a.label);
    const bIndex = path.recommendedPrefs.indexOf(b.label);
    return (
      (aIndex === -1 ? PREF_OPTIONS.length : aIndex) -
      (bIndex === -1 ? PREF_OPTIONS.length : bIndex)
    );
  });
}

type TarotCard = { glyph: string; name: string; meaning: string; position: string };

const TAROT_DECK: TarotCard[] = [
  {
    glyph: '☾',
    name: 'The Moon',
    meaning: 'Intuition, hidden truths surfacing',
    position: '01 · ROOT',
  },
  { glyph: '✦', name: 'The Star', meaning: 'Hope, renewal, gentle guidance', position: '02 · NOW' },
  {
    glyph: '☉',
    name: 'The Sun',
    meaning: 'Clarity, vitality, things aligning',
    position: '03 · OPENING',
  },
  {
    glyph: '♡',
    name: 'The Lovers',
    meaning: 'Connection, choice, heart meeting mind',
    position: '01 · ROOT',
  },
  {
    glyph: '⚡',
    name: 'The Tower',
    meaning: 'Sudden change, old structures falling',
    position: '02 · NOW',
  },
  {
    glyph: '🌍',
    name: 'The World',
    meaning: 'Completion, arrival, wholeness',
    position: '03 · OPENING',
  },
];

type Testimonial = {
  initials: string;
  name: string;
  tag: string;
  stars: number;
  body: string;
};

const TESTIMONIALS: Testimonial[] = [
  {
    initials: 'CA',
    name: 'Carla A.',
    tag: 'Seeking clarity',
    stars: 5,
    body: "I cancelled three other astrology apps after the first week. This is the first one that actually read my whole chart — sun, moon, rising, all of it. The oracle named things I hadn't told anyone.",
  },
  {
    initials: 'MR',
    name: 'Mariana R.',
    tag: 'Navigating a breakup',
    stars: 5,
    body: "The compatibility reading was eerily accurate. It named the dynamic I'd been circling for months. I keep coming back for the daily cosmic weather — it helps me start the day grounded instead of anxious.",
  },
  {
    initials: 'JS',
    name: 'Júlia S.',
    tag: 'Spiritual beginner',
    stars: 5,
    body: "I didn't know my moon sign before this. Now I understand why I feel things so deeply — it was written in my chart the whole time. The tarot pulls are spot-on for the questions I bring.",
  },
];

const ZODIAC_GLYPHS: Record<string, string> = {
  Aries: '♈',
  Taurus: '♉',
  Gemini: '♊',
  Cancer: '♋',
  Leo: '♌',
  Virgo: '♍',
  Libra: '♎',
  Scorpio: '♏',
  Sagittarius: '♐',
  Capricorn: '♑',
  Aquarius: '♒',
  Pisces: '♓',
};

// ── Helpers ──────────────────────────────────────────

function shuffleDeck(deck: TarotCard[]): TarotCard[] {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function toggleString(strings: Set<string>, label: string): Set<string> {
  const next = new Set(strings);
  if (next.has(label)) {
    next.delete(label);
  } else {
    next.add(label);
  }
  return next;
}

function getZodiacMeta(signName: string | null) {
  if (!signName) {
    return null;
  }
  return ZODIAC_SIGNS_DATA.find((s) => s.name === signName) ?? null;
}

function StarsRow({ count }: { count: number }) {
  return (
    <div className={styles.starsRow}>
      {STAR_SLOTS.slice(0, count).map((id) => (
        <Star aria-hidden="true" fill="currentColor" key={id} size={14} />
      ))}
    </div>
  );
}

// ── Screen components ────────────────────────────────

type ScreenProps = {
  onBack: () => void;
  onNext: () => void;
};

function GoalScreen({
  goal,
  setGoal,
  onNext,
}: ScreenProps & {
  goal: string | null;
  setGoal: (label: string) => void;
}) {
  const copy = useOnboardingCopy();
  return (
    <div>
      <p className={styles.eyebrow}>{copy.goalEyebrow}</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>{copy.goalTitle}</h2>
      <p className={styles.lead}>{copy.goalLead}</p>
      <div className={styles.choiceList}>
        {GOAL_OPTIONS.map((opt) => {
          const active = goal === opt.label;
          return (
            <button
              className={`${styles.choice} ${active ? styles.choiceActive : ''}`}
              key={opt.label}
              onClick={() => setGoal(opt.label)}
              type="button"
            >
              <span className={styles.choiceEmoji}>{opt.emoji}</span>
              <span className={styles.choiceLabel}>
                <strong>{copy.choiceLabels[opt.label] ?? opt.label}</strong>
                <small>{copy.choiceLabels[opt.sub] ?? opt.sub}</small>
              </span>
              <span className={styles.choiceCheck}>
                {active && <Check aria-hidden="true" size={14} />}
              </span>
            </button>
          );
        })}
      </div>
      <div className={styles.actions}>
        <button className={styles.primaryButton} disabled={!goal} onClick={onNext} type="button">
          {copy.continue} <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

function PainScreen({
  pains,
  togglePain,
  onNext,
}: ScreenProps & {
  pains: Set<string>;
  togglePain: (label: string) => void;
}) {
  const copy = useOnboardingCopy();
  return (
    <div>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>{copy.painTitle}</h2>
      <p className={styles.lead}>{copy.painLead}</p>
      <div className={styles.choiceList}>
        {PAIN_OPTIONS.map((opt) => {
          const active = pains.has(opt.label);
          return (
            <button
              className={`${styles.choice} ${active ? styles.choiceActive : ''}`}
              key={opt.label}
              onClick={() => togglePain(opt.label)}
              type="button"
            >
              <span className={styles.choiceEmoji}>{opt.emoji}</span>
              <span className={styles.choiceLabel}>
                <strong>{copy.choiceLabels[opt.label] ?? opt.label}</strong>
                <small>{copy.choiceLabels[opt.sub] ?? opt.sub}</small>
              </span>
              <span className={styles.choiceCheck}>
                {active && <Check aria-hidden="true" size={14} />}
              </span>
            </button>
          );
        })}
      </div>
      <div className={styles.actions}>
        <button
          className={styles.primaryButton}
          disabled={pains.size === 0}
          onClick={onNext}
          type="button"
        >
          Continue <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

function SolutionScreen({
  onNext,
  journeyPath,
  pains,
}: ScreenProps & {
  journeyPath: JourneyPath;
  pains: Set<string>;
}) {
  const copy = useOnboardingCopy();
  const relevantMappings = SOLUTION_MAPPINGS.filter((item) => pains.has(item.pain.slice(1, -1)));
  const solutionMappings = relevantMappings.length > 0 ? relevantMappings : SOLUTION_MAPPINGS;

  return (
    <div>
      <p className={styles.eyebrow}>{copy.solutionEyebrow}</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>{journeyPath.solutionTitle}</h2>
      <p className={styles.lead}>{journeyPath.solutionLead}</p>
      <div className={styles.solutionList}>
        {solutionMappings.map((item) => (
          <div className={styles.solutionItem} key={item.pain}>
            <span className={styles.solutionIcon}>{item.icon}</span>
            <div>
              <p className={styles.solutionPain}>{item.pain}</p>
              <p className={styles.solutionFix}>{item.fix}</p>
              <p className={styles.solutionPain} style={{ marginTop: '0.4rem' }}>
                <em style={{ fontStyle: 'normal', color: 'var(--violet-soft)', fontWeight: 700 }}>
                  {item.stat}
                </em>
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Inline social proof */}
      <div className={styles.testimonials}>
        {TESTIMONIALS.map((t) => (
          <div className={styles.testimonial} key={t.name}>
            <div className={styles.testimonialHeader}>
              <span className={styles.testimonialAvatar}>{t.initials}</span>
              <span>
                <span className={styles.testimonialName}>{t.name}</span>
                <br />
                <span className={styles.testimonialTag}>{t.tag.toUpperCase()}</span>
              </span>
            </div>
            <StarsRow count={t.stars} />
            <p className={styles.testimonialBody}>&ldquo;{t.body}&rdquo;</p>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <button className={styles.primaryButton} onClick={onNext} type="button">
          {copy.continue} <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

function PreferencesScreen({
  prefs,
  togglePref,
  onNext,
  journeyPath,
}: ScreenProps & {
  prefs: Set<string>;
  togglePref: (label: string) => void;
  journeyPath: JourneyPath;
}) {
  const copy = useOnboardingCopy();
  const options = orderedPreferences(journeyPath);
  return (
    <div>
      <p className={styles.eyebrow}>{copy.preferencesEyebrow}</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>{copy.preferencesTitle}</h2>
      <p className={styles.lead}>{copy.preferencesLead}</p>
      <div className={styles.prefGrid}>
        {options.map((opt) => {
          const active = prefs.has(opt.label);
          return (
            <button
              className={`${styles.prefCard} ${active ? styles.prefActive : ''}`}
              key={opt.label}
              onClick={() => togglePref(opt.label)}
              type="button"
            >
              <span className={styles.prefIcon}>{opt.emoji}</span>
              <span className={styles.prefLabel}>{copy.choiceLabels[opt.label] ?? opt.label}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.actions}>
        <button
          className={styles.primaryButton}
          disabled={prefs.size === 0}
          onClick={onNext}
          type="button"
        >
          {copy.continue} <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

// ── Birth Data: single form ─────────────────────────

export type OnboardingData = {
  name: string;
  dob: string;
  birthTime: string;
  birthPlace: string;
};

type BirthData = OnboardingData;

function BirthDataScreen({
  birthData,
  setBirthData,
  onNext,
  journeyPath,
}: ScreenProps & {
  birthData: BirthData;
  setBirthData: (data: BirthData) => void;
  journeyPath: JourneyPath;
}) {
  const copy = useOnboardingCopy();
  const zodiacSign = useMemo(() => {
    if (!birthData.dob) {
      return null;
    }
    return getZodiacSignFromDate(birthData.dob);
  }, [birthData.dob]);
  const zodiacMeta = getZodiacMeta(zodiacSign);
  const isValid =
    birthData.name.trim().length > 0 &&
    birthData.dob.length > 0 &&
    birthData.birthPlace.trim().length > 0;

  const update = (field: keyof BirthData, value: string) => {
    setBirthData({ ...birthData, [field]: value });
  };

  return (
    <div>
      <p className={styles.eyebrow}>{copy.birthEyebrow}</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>{copy.birthTitle}</h2>
      <p className={styles.lead}>{copy.birthLead}</p>

      <div className={styles.birthForm}>
        <label className={styles.birthField}>
          <span>{copy.name}</span>
          <input
            autoComplete="off"
            className={styles.ritualInput}
            onChange={(e) => update('name', e.target.value)}
            placeholder={copy.name}
            type="text"
            value={birthData.name}
          />
        </label>

        <label className={styles.birthField}>
          <span>{copy.dob}</span>
          <input
            className={styles.ritualInput}
            max="2025-12-31"
            min="1890-01-01"
            onChange={(e) => update('dob', e.target.value)}
            type="date"
            value={birthData.dob}
          />
        </label>

        <label className={styles.birthField}>
          <span>
            {copy.time} <small>{copy.optional}</small>
          </span>
          <input
            className={styles.ritualInput}
            onChange={(e) => update('birthTime', e.target.value)}
            placeholder="07:42"
            type="time"
            value={birthData.birthTime}
          />
        </label>

        <label className={styles.birthField}>
          <span>{copy.place}</span>
          <input
            autoComplete="off"
            className={styles.ritualInput}
            onChange={(e) => update('birthPlace', e.target.value)}
            placeholder={copy.cityPlaceholder}
            type="text"
            value={birthData.birthPlace}
          />
        </label>
      </div>

      {zodiacMeta && (
        <motion.div
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className={styles.zodiacReveal}
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          transition={{ duration: 0.4 }}
        >
          <span className={styles.zodiacRevealGlyph}>{ZODIAC_GLYPHS[zodiacMeta.name]}</span>
          <div className={styles.zodiacRevealText}>
            <strong>{zodiacMeta.name}</strong>
            <small>
              {zodiacMeta.dateRange} · {zodiacMeta.element}
            </small>
          </div>
        </motion.div>
      )}

      <div className={styles.actions}>
        <button className={styles.primaryButton} disabled={!isValid} onClick={onNext} type="button">
          {copy.continue} <ArrowRight aria-hidden="true" size={18} />
        </button>
        {!birthData.birthTime && (
          <p className={styles.textLink} style={{ cursor: 'default' }}>
            Birth time reveals your rising sign. If you don&apos;t know it, we&apos;ll use solar
            houses.
          </p>
        )}
      </div>
    </div>
  );
}

function ProcessingScreen({
  goal,
  pains,
  prefs,
  journeyPath,
}: {
  goal: string | null;
  pains: Set<string>;
  prefs: Set<string>;
  journeyPath: JourneyPath;
}) {
  const firstPain = [...pains][0];
  return (
    <div className={styles.processingWrap}>
      <div className={styles.processingRing}>
        <svg aria-hidden="true" className={styles.processingRingSvg} viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            fill="none"
            r="45"
            stroke="rgba(155,99,255,0.15)"
            strokeWidth="2"
          />
          <circle
            cx="50"
            cy="50"
            fill="none"
            r="45"
            stroke="url(#procGrad)"
            strokeDasharray="120 200"
            strokeLinecap="round"
            strokeWidth="2"
          />
          <defs>
            <linearGradient id="procGrad" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stopColor="#9b63ff" />
              <stop offset="100%" stopColor="#ef63c8" />
            </linearGradient>
          </defs>
        </svg>
        <div className={styles.processingCenter}>
          <Sparkles aria-hidden="true" size={28} />
        </div>
      </div>
      <div>
        <p className={`${styles.processingLabel} ${styles.fontDisplay}`}>
          {journeyPath.processingLabel}
        </p>
        <p className={styles.processingSub}>{journeyPath.processingSub}</p>
        <ul className={styles.processingSteps}>
          <li>{goal ?? 'Your intention'} → personal lens</li>
          <li>{firstPain ?? 'Your honest answer'} → useful context</li>
          <li>{prefs.size || 1} chosen interests → next reading</li>
        </ul>
      </div>
    </div>
  );
}

function DemoScreen({
  demoPicks,
  toggleDemoPick,
  demoShuffle,
  journeyPath,
  onNext,
}: ScreenProps & {
  demoPicks: number[];
  toggleDemoPick: (idx: number) => void;
  demoShuffle: TarotCard[];
  journeyPath: JourneyPath;
}) {
  const remaining = DEMO_PICK_LIMIT - demoPicks.length;
  return (
    <div>
      <div className={styles.demoIntro}>
        <p className={styles.eyebrow}>STEP 6 · YOUR FIRST READING</p>
        <h2 className={`${styles.title} ${styles.fontDisplay}`}>
          Draw <em>three cards.</em>
        </h2>
        <p className={styles.lead} style={{ margin: '0 auto' }}>
          {journeyPath.demoPrompt}
        </p>
        <div className={styles.demoCount}>
          {remaining > 0 ? `Pick ${remaining} more` : 'All three drawn!'}
        </div>
      </div>
      <div className={styles.tarotSpread}>
        {demoShuffle.slice(0, DEMO_VISIBLE_CARDS).map((card, idx) => {
          const selected = demoPicks.includes(idx);
          return (
            <button
              className={`${styles.tarotCard} ${selected ? styles.tarotCardSelected : ''}`}
              disabled={demoPicks.length >= DEMO_PICK_LIMIT && !selected}
              key={`${card.name}-${card.position}`}
              onClick={() => toggleDemoPick(idx)}
              type="button"
            >
              <span className={styles.tarotCardPos}>{card.position}</span>
              <span className={styles.tarotCardFace}>{card.glyph}</span>
              <strong className={styles.tarotCardName}>{card.name}</strong>
              <small className={styles.tarotCardMeaning}>{card.meaning}</small>
            </button>
          );
        })}
      </div>
      <div className={styles.actions}>
        <button
          className={styles.primaryButton}
          disabled={demoPicks.length !== DEMO_PICK_LIMIT}
          onClick={onNext}
          type="button"
        >
          Reveal my reading <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

function ValueScreen({
  demoPicks,
  demoShuffle,
  birthData,
  journeyPath,
  onNext,
}: ScreenProps & {
  demoPicks: number[];
  demoShuffle: TarotCard[];
  birthData: BirthData;
  journeyPath: JourneyPath;
}) {
  const pickedCards = demoPicks.map((idx) => demoShuffle[idx]);
  const zodiacSign = birthData.dob ? getZodiacSignFromDate(birthData.dob) : null;
  const zodiacMeta = getZodiacMeta(zodiacSign);
  const firstName = birthData.name.trim().split(' ')[0] || 'you';
  const signName = zodiacMeta?.name ?? 'your sign';

  const elementIntro: Record<string, string> = {
    Fire: 'There is a fire in you that refuses to wait for permission.',
    Earth: 'You build slowly, but what you build tends to last.',
    Air: 'Your mind moves faster than your mouth can follow.',
    Water: 'You feel things before you can name them.',
  };
  const elementInstinct: Record<string, string> = {
    Fire: 'act first, reflect later',
    Earth: 'build a plan before moving',
    Air: 'talk it through until it makes sense',
    Water: 'sit with it until the feeling clarifies',
  };
  const intro = zodiacMeta
    ? (elementIntro[zodiacMeta.element] ?? 'Something is shifting.')
    : 'Something is shifting.';
  const instinct = zodiacMeta
    ? (elementInstinct[zodiacMeta.element] ?? 'trust your gut')
    : 'trust your gut';

  return (
    <div>
      <p className={styles.eyebrow}>YOUR READING · JUST NOW</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>
        {firstName}, here&apos;s what <em>the cards see.</em>
      </h2>
      <p className={styles.lead}>{journeyPath.readingLead}</p>

      {zodiacMeta && (
        <div className={styles.signBadge}>
          <span className={styles.signBadgeGlyph}>{ZODIAC_GLYPHS[zodiacMeta.name]}</span>
          <span className={styles.signBadgeText}>
            Reading for <strong>{zodiacMeta.name}</strong> · {zodiacMeta.element}
          </span>
        </div>
      )}

      <div className={styles.valueOutput}>
        <div className={styles.valueHeader}>
          <div className={styles.valueGlyph}>✦</div>
          <div className={`${styles.valueTitle} ${styles.fontDisplay}`}>Three-Card Spread</div>
          <div className={styles.valueSubtitle}>ROOT · NOW · OPENING</div>
        </div>
        <div className={styles.valueCards}>
          {pickedCards.map((card) => (
            <div className={styles.valueCardRow} key={`${card.name}-${card.position}`}>
              <span className={styles.valueCardGlyph}>{card.glyph}</span>
              <div className={styles.valueCardText}>
                <div className={styles.valueCardName}>{card.name}</div>
                <div className={styles.valueCardMeaning}>{card.meaning}</div>
              </div>
            </div>
          ))}
        </div>
        <p className={styles.valueReading} style={{ marginTop: '1rem' }}>
          {intro} The cards suggest something that was hidden is asking to be seen — you don&apos;t
          need to resolve it today. As a {signName}, your instinct might be to {instinct}. The
          opening ahead is real. It&apos;s yours to walk through when you&apos;re ready.
        </p>
      </div>

      <button className={styles.shareButton} type="button">
        <Share2 aria-hidden="true" size={16} /> Share this reading
      </button>

      <div className={styles.actions}>
        <button className={styles.primaryButton} onClick={onNext} type="button">
          Save my reading <ArrowRight aria-hidden="true" size={18} />
        </button>
      </div>
    </div>
  );
}

function AccountScreen({
  demoPicks,
  demoShuffle,
  birthData,
  onNext,
  onAccountContinue,
}: ScreenProps & {
  demoPicks: number[];
  demoShuffle: TarotCard[];
  birthData: BirthData;
  onAccountContinue?: () => void;
}) {
  const t = useTranslations('onboarding.account');
  const handleContinue = onAccountContinue ?? onNext;
  const pickedCards = demoPicks.map((idx) => demoShuffle[idx]);
  const firstName = birthData.name.trim().split(' ')[0] || '';
  const zodiacSign = birthData.dob ? getZodiacSignFromDate(birthData.dob) : null;
  const zodiacMeta = getZodiacMeta(zodiacSign);

  return (
    <div>
      <p className={styles.eyebrow}>{t('eyebrow')}</p>
      <h2 className={`${styles.title} ${styles.fontDisplay}`}>
        {firstName ? `${firstName}, ${t('saveTitle')} ` : `${t('saveTitle')} `}
        <em>{t('saveTitleEm')}</em>
      </h2>

      <div className={styles.accountSummary}>
        <div className={styles.accountSummaryRow}>
          <span className={styles.accountSummaryLabel}>{t('yourReading')}</span>
          <div className={styles.accountThumbs}>
            {pickedCards.map((card) => (
              <div className={styles.accountThumb} key={`thumb-${card.name}`}>
                {card.glyph}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.accountSummaryRow}>
          <span className={styles.accountSummaryLabel}>{t('yourChart')}</span>
          <div className={styles.accountChartInfo}>
            {zodiacMeta && (
              <span className={styles.accountChartSign}>
                <span className={styles.accountChartGlyph}>{ZODIAC_GLYPHS[zodiacMeta.name]}</span>
                {zodiacMeta.name}
              </span>
            )}
            {birthData.dob && <span className={styles.accountChartDetail}>{birthData.dob}</span>}
            {birthData.birthPlace && (
              <span className={styles.accountChartDetail}>{birthData.birthPlace}</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.dataCollectedBadge}>
        <Check aria-hidden="true" size={14} /> {t('savedBadge')}
      </div>

      <div className={styles.accountPerks}>
        <div className={styles.accountPerk}>
          <span className={styles.accountPerkCheck}>
            <Check aria-hidden="true" size={16} />
          </span>
          {t('perk1')}
        </div>
        <div className={styles.accountPerk}>
          <span className={styles.accountPerkCheck}>
            <Check aria-hidden="true" size={16} />
          </span>
          {t('perk2')}
        </div>
        <div className={styles.accountPerk}>
          <span className={styles.accountPerkCheck}>
            <Check aria-hidden="true" size={16} />
          </span>
          {t('perk3')}
        </div>
      </div>

      <div className={styles.actions}>
        <button
          className={`${styles.accountButton} ${styles.accountButtonPrimary}`}
          onClick={handleContinue}
          type="button"
        >
          {t('continueGoogle')}
        </button>
        <button className={styles.accountButton} onClick={handleContinue} type="button">
          {t('continueApple')}
        </button>
        <button className={styles.accountButton} onClick={handleContinue} type="button">
          {t('continueEmail')}
        </button>
        <button className={styles.textLink} onClick={handleContinue} type="button">
          {t('existingAccount')}
        </button>
      </div>
    </div>
  );
}

// ── Paywall ─────────────────────────────────────────

type PaywallScreenProps = {
  birthData: BirthData;
  onRestart: () => void;
};

const PLANS = [
  {
    id: 'cosmic-navigator',
    name: 'Cosmic Navigator',
    tagline: 'A structured practice for clearer decisions',
    badge: 'MOST POPULAR',
    priceMonthly: 11.99,
    priceAnnual: 79.99,
    annualEquiv: 6.67,
    trialDays: 7,
    features: [
      'Ask Elara up to 100× per month',
      'Longer horoscope cycles & timing context',
      'Pattern library for recurring themes',
      '5 relationship readings per month',
      'Unlimited quick tarot spreads',
    ],
  },
  {
    id: 'cosmic-oracle',
    name: 'Cosmic Oracle',
    tagline: 'Deep, on-demand reflection across your map',
    badge: 'PREMIUM',
    priceMonthly: 19.99,
    priceAnnual: 129.99,
    annualEquiv: 10.83,
    trialDays: 7,
    features: [
      'Unlimited guided questions — no monthly cap',
      'Everything in Cosmic Navigator, kept',
      'Expanded relationship readings & timing',
      'Advanced tarot spreads for complex moments',
    ],
  },
] as const;

function PaywallScreen({ birthData, onRestart }: PaywallScreenProps) {
  const t = useTranslations('onboarding.paywallScreen');
  const [selectedPlan, setSelectedPlan] = useState<'cosmic-navigator' | 'cosmic-oracle'>(
    'cosmic-navigator'
  );
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const firstName = birthData.name.trim().split(' ')[0] || '';
  const zodiacSign = birthData.dob ? getZodiacSignFromDate(birthData.dob) : null;
  const zodiacMeta = getZodiacMeta(zodiacSign);

  const activePlan = PLANS.find((p) => p.id === selectedPlan) ?? PLANS[0];

  return (
    <div className={styles.paywallWrap}>
      <div className={styles.paywallLogo}>
        <Orbit aria-hidden="true" size={20} />
      </div>
      <h2 className={`${styles.paywallHeadline} ${styles.fontDisplay}`}>
        {firstName ? `${firstName}, ` : t('chartLoadedPrefix')}
        <em>{t('chartLoaded')}</em>
      </h2>
      <p className={styles.lead} style={{ textAlign: 'center', margin: '0 auto 1.5rem' }}>
        {zodiacMeta
          ? `${t('youAreSign')} ${zodiacMeta.name}. ${t('paywallLeadSign1')}`
          : t('paywallLead1')}
        {t('paywallLead2')}
      </p>

      <div className={styles.paywallTestimonial}>
        <div className={styles.starsRow} style={{ justifyContent: 'center' }}>
          <StarsRow count={STAR_COUNT} />
        </div>
        <p className={styles.testimonialBody} style={{ textAlign: 'center' }}>
          &ldquo;{t('testimonialQuote')}&rdquo;
        </p>
        <p
          style={{
            textAlign: 'center',
            color: 'var(--muted-dim)',
            fontSize: '0.72rem',
            marginTop: '0.4rem',
          }}
        >
          — {t('testimonialName')}, {t('testimonialTag')}
        </p>
      </div>

      <div className={styles.billingToggle}>
        <button
          className={`${styles.billingToggleBtn} ${billingCycle === 'monthly' ? styles.billingToggleActive : ''}`}
          onClick={() => setBillingCycle('monthly')}
          type="button"
        >
          {t('monthly')}
        </button>
        <button
          className={`${styles.billingToggleBtn} ${billingCycle === 'annual' ? styles.billingToggleActive : ''}`}
          onClick={() => setBillingCycle('annual')}
          type="button"
        >
          {t('annual')} <span className={styles.billingSave}>{t('save44')}</span>
        </button>
      </div>

      <div className={styles.planCards}>
        {PLANS.map((plan) => {
          const active = selectedPlan === plan.id;
          return (
            <button
              className={`${styles.planCard} ${active ? styles.planCardActive : ''}`}
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id as 'cosmic-navigator' | 'cosmic-oracle')}
              type="button"
            >
              <div className={styles.planCardHeader}>
                <span className={styles.planCardBadge}>{t(`plans.${plan.id}.badge`)}</span>
                <strong className={`${styles.planCardName} ${styles.fontDisplay}`}>
                  {t(`plans.${plan.id}.name`)}
                </strong>
                <small className={styles.planCardTagline}>{t(`plans.${plan.id}.tagline`)}</small>
              </div>
              <div className={styles.planCardPrice}>
                {billingCycle === 'annual' ? `$${plan.priceAnnual}` : `$${plan.priceMonthly}`}
                <span className={styles.planCardPricePeriod}>
                  {billingCycle === 'annual' ? t('perYear') : t('perMonth')}
                </span>
              </div>
              <div className={styles.planCardFeatures}>
                {plan.features.map((f, idx) => (
                  <div className={styles.planCardFeature} key={f}>
                    <Check aria-hidden="true" size={13} /> {t(`plans.${plan.id}.features.${idx}`)}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      <div className={styles.paywallTrialBadge}>
        <Check aria-hidden="true" size={16} /> {t('trialBadge')}
      </div>

      <div className={styles.actions}>
        <button className={styles.primaryButton} onClick={onRestart} type="button">
          {t('startTrial')} {activePlan.trialDays} {t('startTrialSuffix')}{' '}
          <ArrowRight aria-hidden="true" size={18} />
        </button>
        <button className={styles.textLink} onClick={onRestart} type="button">
          {t('maybeLater')}
        </button>
      </div>
    </div>
  );
}

// ── Main orchestrator ───────────────────────────────

type OnboardingProps = {
  onAccountContinue?: (data: OnboardingData) => void;
  onExistingAccount?: () => void;
  existingAccountLabel?: string;
  startAtGoal?: boolean;
  initialGoal?: string;
};

export default function Onboarding({
  onAccountContinue,
  onExistingAccount,
  existingAccountLabel = 'I already have an account',
  startAtGoal = false,
  initialGoal,
}: OnboardingProps = {}) {
  const initialScreenIdx = startAtGoal ? 0 : 0;
  const [screenIdx, setScreenIdx] = useState(initialGoal ? 1 : initialScreenIdx);
  const [goal, setGoal] = useState<string | null>(initialGoal ?? null);
  const [pains, setPains] = useState<Set<string>>(new Set());
  const [prefs, setPrefs] = useState<Set<string>>(new Set());
  const [birthData, setBirthData] = useState<BirthData>({
    name: '',
    dob: '',
    birthTime: '',
    birthPlace: '',
  });
  const [demoPicks, setDemoPicks] = useState<number[]>([]);
  const [demoShuffle] = useState(() => shuffleDeck(TAROT_DECK));
  const journeyPath = useMemo(
    () => resolveJourneyPath({ goal, pains, prefs }),
    [goal, pains, prefs]
  );

  const screenId = SCREEN_ORDER[screenIdx];
  const progressPhase = SCREEN_PROGRESS_PHASE[screenId];
  const progress = (progressPhase / TOTAL_PROGRESS_PHASES) * PROGRESS_SCALE;

  const goNext = useCallback(() => {
    setScreenIdx((prev) => Math.min(prev + 1, TOTAL_SCREENS - 1));
  }, []);

  const goBack = useCallback(() => {
    setScreenIdx((prev) => Math.max(prev - 1, 0));
  }, []);

  const restart = useCallback(() => {
    setScreenIdx(initialGoal ? 1 : initialScreenIdx);
    setGoal(initialGoal ?? null);
    setPains(new Set());
    setPrefs(new Set());
    setBirthData({ name: '', dob: '', birthTime: '', birthPlace: '' });
    setDemoPicks([]);
  }, [initialScreenIdx, initialGoal]);

  const togglePain = useCallback((label: string) => {
    setPains((prev) => toggleString(prev, label));
  }, []);

  const togglePref = useCallback((label: string) => {
    setPrefs((prev) => toggleString(prev, label));
  }, []);

  const toggleDemoPick = useCallback((idx: number) => {
    setDemoPicks((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((p) => p !== idx);
      }
      if (prev.length >= DEMO_PICK_LIMIT) {
        return prev;
      }
      return [...prev, idx];
    });
  }, []);

  // Auto-advance from processing screen
  useEffect(() => {
    if (screenId !== 'processing') {
      return;
    }
    const timer = setTimeout(() => {
      goNext();
    }, PROCESSING_AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [screenId, goNext]);

  const showProgress = screenIdx > 0 && screenId !== 'paywall';
  const showBack = screenIdx > 0 && screenId !== 'processing';

  const screenProps: ScreenProps = { onBack: goBack, onNext: goNext };

  const renderScreen = () => {
    switch (screenId) {
      case 'goal':
        return <GoalScreen {...screenProps} goal={goal} setGoal={setGoal} />;
      case 'pain':
        return <PainScreen {...screenProps} pains={pains} togglePain={togglePain} />;
      case 'solution':
        return <SolutionScreen {...screenProps} journeyPath={journeyPath} pains={pains} />;
      case 'preferences':
        return (
          <PreferencesScreen
            {...screenProps}
            journeyPath={journeyPath}
            prefs={prefs}
            togglePref={togglePref}
          />
        );
      case 'birthData':
        return (
          <BirthDataScreen
            {...screenProps}
            birthData={birthData}
            journeyPath={journeyPath}
            setBirthData={setBirthData}
          />
        );
      case 'processing':
        return (
          <ProcessingScreen goal={goal} journeyPath={journeyPath} pains={pains} prefs={prefs} />
        );
      case 'demo':
        return (
          <DemoScreen
            {...screenProps}
            demoPicks={demoPicks}
            demoShuffle={demoShuffle}
            journeyPath={journeyPath}
            toggleDemoPick={toggleDemoPick}
          />
        );
      case 'value':
        return (
          <ValueScreen
            {...screenProps}
            birthData={birthData}
            demoPicks={demoPicks}
            demoShuffle={demoShuffle}
            journeyPath={journeyPath}
          />
        );
      case 'account':
        return (
          <AccountScreen
            {...screenProps}
            birthData={birthData}
            demoPicks={demoPicks}
            demoShuffle={demoShuffle}
            onAccountContinue={onAccountContinue ? () => onAccountContinue(birthData) : undefined}
          />
        );
      case 'paywall':
        return <PaywallScreen birthData={birthData} onRestart={restart} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.shell}>
      <div aria-hidden="true" className={styles.starField} />
      <div className={styles.container}>
        {showProgress && (
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
        )}

        {showBack && (
          <button className={styles.backButton} onClick={goBack} type="button">
            <ArrowLeft aria-hidden="true" size={16} /> Back
          </button>
        )}
        {screenId === 'goal' && onExistingAccount && (
          <button
            className={styles.textLink}
            onClick={onExistingAccount}
            style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}
            type="button"
          >
            {existingAccountLabel}
          </button>
        )}

        <AnimatePresence mode="popLayout">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className={styles.screen}
            exit={{ opacity: 0, y: -8 }}
            initial={{ opacity: 0, y: 8 }}
            key={screenId}
            transition={{ duration: SCREEN_TRANSITION_DURATION, ease: SCREEN_EASE_CURVE }}
          >
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
